import {Cents} from "./transaction"
import {createResource, getResource, Resource} from "./common"

export enum IncomingRealTimePaymentStatus {
	Pending = "Pending",
	AwaitingConfirmation = "AwaitingConfirmation",
	Processed = "Processed",
	Rejected = "Rejected",
	Cancelled = "Cancelled",
	Errored = "Errored",
	PendingReview = "PendingReview",
	Returned = "Returned",
	TimedOut = "TimedOut",
}

export const INCOMING_RTP_RESOURCE = "incoming-real-time-payments"

export enum IncomingRealTimePaymentTypes {
	CreditTransfer = "incomingRealTimePayment",
	RequestForReturnOfFunds = "requestForReturnOfFunds",
}

export function incomingRealTimePaymentTypeLabel(type: IncomingRealTimePaymentTypes): string {
	switch (type) {
		case IncomingRealTimePaymentTypes.CreditTransfer:
			return "Credit Transfer"
		case IncomingRealTimePaymentTypes.RequestForReturnOfFunds:
			return "Return Request"
	}
}

const INCOMING_RTP_LIST_TYPES = [
	IncomingRealTimePaymentTypes.CreditTransfer,
	IncomingRealTimePaymentTypes.RequestForReturnOfFunds,
]

export async function findIncomingRealTimePayments(
	accessToken: string,
	offset: number,
	limit?: number | null,
	statusFilter?: IncomingRealTimePaymentStatus[],
	orgs?: string[],
	banks?: string[],
	fromAmount?: Cents | "",
	toAmount?: Cents | "",
	since?: string,
	until?: string,
	accountId?: string,
	customerId?: string,
	bankId?: string
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const hasMultipleStatuses = (statusFilter?.length ?? 0) > 1
	const hasMultipleBanks = (banks?.length ?? 0) > 1

	const query = {
		page: {
			limit: limit,
			offset,
		},
		filter: {
			status: statusFilter?.length === 1 ? statusFilter[0] : null,
			statuses: hasMultipleStatuses ? statusFilter : undefined,
			types: INCOMING_RTP_LIST_TYPES,
			accountId: accountId,
			customerId: customerId,
			bankId: banks?.length === 1 ? banks[0] : bankId,
			orgs: orgFilter,
			banks: hasMultipleBanks ? banks : undefined,
			fromAmount: fromAmount != "" ? fromAmount : null,
			toAmount: toAmount != "" ? toAmount : null,
			since: since != "" ? since : null,
			until: until != "" ? until : null,
		},
		include: "bank,account,customer,org",
	}
	return await getResource<Array<IncomingRealTimePayment>>(INCOMING_RTP_RESOURCE, accessToken, query)
}

export async function findIncomingRealTimePaymentByRelatedInstructionId(
	accessToken: string,
	relatedInstructionId: string
) {
	const query = {
		page: {
			limit: 10,
			offset: 0,
		},
		filter: {
			instructionId: relatedInstructionId,
			types: [IncomingRealTimePaymentTypes.CreditTransfer],
		},
	}
	return await getResource<Array<IncomingRealTimePayment>>(INCOMING_RTP_RESOURCE, accessToken, query)
}

export function matchingCreditTransferId(
	payments: IncomingRealTimePayment[],
	relatedInstructionId: string
): string | undefined {
	return payments.find((payment) => payment.attributes.instructionId === relatedInstructionId)?.id
}

export enum IncomingRealTimePaymentRejectReason {
	AccountClosed = "AC04",
	AccountNotActive = "AM04",
	AccountNotAuthorized = "ARDT",
	CustomerNotFound = "CUST",
	LegalCompliance = "LEGL",
	NoAccountStatement = "NOAS",
	NoOtherReason = "NOOR",
}

//todo: figure out which message types are supported
export enum IncomingRealTimePaymentMessageType {
	RequestForReturnOfFunds = "RequestForReturnOfFunds",
}

export async function getIncomingRealTimePayment(accessToken: string, incomingRealTimePaymentId: string) {
	const query = {
		include: "bank,account,customer,org,transaction",
	}
	return await getResource<IncomingRealTimePayment>(
		`${INCOMING_RTP_RESOURCE}/${incomingRealTimePaymentId}`,
		accessToken,
		query
	)
}

export async function approveIncomingRealTimePayment(
	accessToken: string,
	incomingRealTimePaymentId: string,
	instructionId: string,
	idempotencyKey: string
) {
	const data = {
		type: "approveReturnOfFundsRealTimePayment",
		attributes: {
			instructionId: instructionId,
			idempotencyKey: idempotencyKey,
		},
	}
	return await createResource<IncomingRealTimePayment>(
		`${INCOMING_RTP_RESOURCE}/${incomingRealTimePaymentId}/approve`,
		accessToken,
		data
	)
}

export async function rejectIncomingRealTimePayment(
	accessToken: string,
	incomingRealTimePaymentId: string,
	instructionId: string,
	idempotencyKey: string,
	reason: IncomingRealTimePaymentRejectReason
) {
	const data = {
		type: "rejectReturnOfFundsRealTimePayment",
		attributes: {
			instructionId: instructionId,
			rejectReason: reason,
			idempotencyKey: idempotencyKey,
		},
	}
	return await createResource<IncomingRealTimePayment>(
		`${INCOMING_RTP_RESOURCE}/${incomingRealTimePaymentId}/reject`,
		accessToken,
		data
	)
}

export function canReviewIncomingRealTimePaymentReturnRequest(rtp: IncomingRealTimePayment): boolean {
	return (
		rtp.attributes.messageType === IncomingRealTimePaymentMessageType.RequestForReturnOfFunds &&
		rtp.attributes.status === IncomingRealTimePaymentStatus.PendingReview
	)
}

export type IncomingRealTimePayment = {
	type: IncomingRealTimePaymentTypes
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		messageId: string
		instructionId: string
		amount: Cents
		status: IncomingRealTimePaymentStatus
		messageType?: IncomingRealTimePaymentMessageType
		isMonetary?: boolean
		relatedCreditTransferInstructionId?: string | null
		returnRequestReasonCode?: string | null
		creditTransferRejectReason?: {code: IncomingRealTimePaymentRejectReason; description: string} | null
		returnRequestRejectReason?: IncomingRealTimePaymentRejectReason | null
		errorReason?: string | null
		counterpartyName?: string | null
		counterpartyRoutingNumber?: string | null
		counterpartyAccountNumber?: string | null
		incomingMessage?: object | null
		bankRoutingNumber?: string | null
		settlementId?: string | null
		uniqueETETxRef?: string | null
	}
	relationships: {
		bank: {
			data: {
				type: string
				id: string
			}
		}
		account?: {
			data: {
				type: string
				id: string | null
			}
		}
		customer?: {
			data: {
				type: string
				id: string | null
			}
		}
		org?: {
			data: {
				type: string
				id: string | null
			}
		}
		transaction?: {
			data: {
				type: string
				id: string | null
			}
		}
	}
}

export function isIncomingRealTimePayment(resource: Resource): resource is IncomingRealTimePayment {
	return (
		resource.type === IncomingRealTimePaymentTypes.CreditTransfer ||
		resource.type === IncomingRealTimePaymentTypes.RequestForReturnOfFunds
	)
}
